import { useCallback, useEffect, useState } from 'react';

type Theme = 'light' | 'dark' | 'system';

function resolvedTheme(t: Theme): 'light' | 'dark' {
  if (t !== 'system') return t;
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

// хук темы: хранит выбор юзера в localStorage, 'system' резолвится через matchMedia
export function useTheme() {
  const [theme, setThemeState] = useState<Theme>(() => {
    return (localStorage.getItem('theme') as Theme) ?? 'system';
  });

  const applyTheme = useCallback((t: Theme) => {
    document.documentElement.classList.toggle('dark', resolvedTheme(t) === 'dark');
  }, []);

  useEffect(() => {
    applyTheme(theme);
    localStorage.setItem('theme', theme);
  }, [theme, applyTheme]);

  useEffect(() => {
    if (theme !== 'system') return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = () => applyTheme('system');
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, [theme, applyTheme]);

  const setTheme = useCallback((t: Theme) => setThemeState(t), []);
  const toggleTheme = useCallback(() => {
    setThemeState(prev => resolvedTheme(prev) === 'dark' ? 'light' : 'dark');
  }, []);

  return { theme, resolvedTheme: resolvedTheme(theme), setTheme, toggleTheme };
}
